import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentActivityPanel = ({ activities }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case 'valuation':
        return { icon: 'Calculator', color: 'text-primary' };
      case 'alert':
        return { icon: 'Bell', color: 'text-warning' };
      case 'report':
        return { icon: 'FileText', color: 'text-success' };
      case 'batch_job':
        return { icon: 'RefreshCw', color: 'text-accent' };
      case 'asset_added':
        return { icon: 'Plus', color: 'text-success' };
      case 'asset_removed':
        return { icon: 'Minus', color: 'text-destructive' };
      default:
        return { icon: 'Activity', color: 'text-text-secondary' };
    }
  };

  const formatActivityTime = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMinutes = Math.floor((now - activityTime) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return activityTime?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getActivityAction = (activity) => {
    switch (activity?.type) {
      case 'valuation': case'report':
        return (
          <Button
            variant="ghost"
            size="sm"
            iconName="ExternalLink"
            className="text-text-secondary hover:text-text-primary"
          />
        );
      case 'batch_job':
        return activity?.status === 'completed' ? (
          <Button
            variant="ghost"
            size="sm"
            iconName="Download"
            className="text-text-secondary hover:text-text-primary"
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">Recent Activity</h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="MoreHorizontal"
            className="text-text-secondary hover:text-text-primary"
          />
        </div>
      </div>
      <div className="p-6">
        {activities?.length > 0 ? (
          <div className="space-y-4">
            {activities?.map((activity) => {
              const activityIcon = getActivityIcon(activity?.type);
              return (
                <div key={activity?.id} className="flex items-start space-x-4">
                  <div className={`p-2 rounded-lg bg-muted ${activityIcon?.color}`}>
                    <Icon name={activityIcon?.icon} size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-text-primary">
                          {activity?.title}
                        </p>
                        <p className="text-sm text-text-secondary mt-1">
                          {activity?.description}
                        </p>
                        <div className="flex items-center space-x-3 mt-2">
                          <span className="text-xs text-text-secondary">
                            {formatActivityTime(activity?.timestamp)}
                          </span>
                          {activity?.user && (
                            <span className="text-xs text-text-secondary">
                              by {activity?.user}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="ml-4">
                        {getActivityAction(activity)}
                      </div>
                    </div>
                    
                    {activity?.metadata && (
                      <div className="mt-3 p-3 bg-muted rounded border border-border">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {Object.entries(activity?.metadata)?.map(([key, value]) => (
                            <div key={key}>
                              <span className="font-medium text-text-primary capitalize">
                                {key?.replace('_', ' ')}: 
                              </span>
                              <span className="text-text-secondary ml-1">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon name="Activity" size={32} className="mx-auto mb-2 text-text-secondary opacity-50" />
            <p className="text-text-secondary">No recent activity</p>
          </div>
        )}

        {activities?.length > 0 && (
          <div className="mt-6 pt-6 border-t border-border text-center">
            <Button
              variant="ghost"
              size="sm"
              iconName="ArrowRight"
              iconPosition="right"
            >
              View All Activity
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentActivityPanel;